<script setup lang="ts">
import emptyOptionImage from "@/assets/img/picture-choice-empty.svg?url";
import otherOptionImage from "@/assets/img/picture-choice-other.svg?url";
import { useIMEComposition } from "@/composition/useIMEComposition";
import { MediaCenterSubCategory } from "@/helpers/mediaCenter.type";
import { useAppStore } from "@/store/app";
import type {
  PictureChoiceElement,
  PictureChoiceOption,
} from "@/types/pictureChoice";
import { PICTURE_CHOICE_DEFAULT_IMAGE_FIT } from "@/types/pictureChoice";
import {
  clampPictureChoiceColumnsForDevice,
  createPictureChoiceOption,
  createPictureChoiceOtherOption,
  createUniquePictureChoiceOptionLabel,
  normalizePictureChoiceElement,
  updatePictureChoiceOptionLabel,
} from "@/util/pictureChoice";
import {
  applyPictureChoiceMediaSelection,
  openPictureChoiceMediaCenter,
  type PictureChoiceMediaSelection,
} from "@/util/pictureChoiceMedia";
import {
  fixSafariCursor,
  getCaretOffsetWithin,
  setCaretOffsetWithin,
} from "@/util/methods";
import {
  Image01Icon,
  PlusIcon,
  Trash01Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UITooltip } from "@gohighlevel/ghl-ui";
import { cloneDeep } from "lodash";
import {
  computed,
  getCurrentInstance,
  nextTick,
  onBeforeUnmount,
  ref,
  type CSSProperties,
  type PropType,
} from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps({
  element: {
    type: Object as PropType<PictureChoiceElement>,
    required: true,
  },
  isActive: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits<{
  (
    event: "update:element",
    element: PictureChoiceElement,
    addToHistory?: boolean
  ): void;
}>();

const { t } = useI18n();
const store = useAppStore();
const componentInstanceId = getCurrentInstance()?.uid ?? 0;
const { handleCompositionStart, handleCompositionEnd, withIMECheck } =
  useIMEComposition();

interface RenderablePictureChoiceOption {
  option: PictureChoiceOption;
  isOther: boolean;
}

const options = computed<RenderablePictureChoiceOption[]>(() => {
  const regularOptions = props.element.picklistOptionsImage ?? [];
  const renderableOptions = regularOptions.map((option) => ({
    option,
    isOther: false,
  }));

  return props.element.allowCustomOption
    ? [
        ...renderableOptions,
        {
          option: createPictureChoiceOtherOption(),
          isOther: true,
        },
      ]
    : renderableOptions;
});

const inputType = computed(() =>
  props.element.dataType === "CHECKBOX" ? "checkbox" : "radio"
);

const imageFit = computed(
  () =>
    props.element.pictureChoiceSettings?.imageFit ??
    PICTURE_CHOICE_DEFAULT_IMAGE_FIT
);

const showOptionLabel = computed(
  () => props.element.pictureChoiceSettings?.showOptionLabel ?? true
);

let hasUncommittedOptionLabelChanges = false;
const updateElement = (
  mutate: (element: PictureChoiceElement) => void,
  addToHistory = true
) => {
  if (addToHistory) hasUncommittedOptionLabelChanges = false;

  const nextElement = cloneDeep(props.element);
  mutate(nextElement);
  emit(
    "update:element",
    normalizePictureChoiceElement(nextElement) as PictureChoiceElement,
    addToHistory
  );
};

const addOptionAt = (index: number) => {
  const insertAt = Math.min(
    Math.max(index + 1, 0),
    props.element.picklistOptionsImage.length
  );

  updateElement((element) => {
    const label = createUniquePictureChoiceOptionLabel(
      element.picklistOptionsImage
    );
    element.picklistOptionsImage.splice(
      insertAt,
      0,
      createPictureChoiceOption(label)
    );
  });

  nextTick(() => {
    document.getElementById(`${getOptionId(insertAt)}-label`)?.focus();
  });
};

const addOption = () => {
  addOptionAt(props.element.picklistOptionsImage.length - 1);
};

const handleOptionLabelEnter = (
  event: KeyboardEvent,
  index: number,
  isOther: boolean
) => {
  if (
    !props.isActive ||
    isOther ||
    event.isComposing ||
    event.keyCode === 229
  ) {
    return;
  }

  event.preventDefault();
  event.stopPropagation();
  addOptionAt(index);
};

const labelRefs = ref<Array<HTMLElement | null>>([]);
const mediaRefs = ref<Array<HTMLElement | null>>([]);
const hoveredOptionIndex = ref<number | null>(null);
const focusedOptionIndex = ref<number | null>(null);

const isFilledImageActionsVisible = (index: number) =>
  hoveredOptionIndex.value === index || focusedOptionIndex.value === index;

const setLabelRef = (element: Element | null, index: number) => {
  labelRefs.value[index] = element as HTMLElement | null;
};

const setMediaRef = (element: Element | null, index: number) => {
  mediaRefs.value[index] = element as HTMLElement | null;
};

const handleSafariCursor = (event: Event) => {
  fixSafariCursor(event, event.type === "keydown");
};

const handleOptionLabelFocus = (event: FocusEvent, isOther: boolean) => {
  if (!props.isActive || isOther) return;

  const target = event.target as HTMLElement;
  const selection = window.getSelection();
  if (!selection) return;

  selection.removeAllRanges();
  const sanitized = (target.textContent ?? "").replace(/\u200B/g, "").trim();

  if (!sanitized) {
    let textNode = target.firstChild as Text | null;
    if (!textNode || textNode.nodeType !== Node.TEXT_NODE) {
      target.textContent = "";
      textNode = document.createTextNode("\u200B");
      target.appendChild(textNode);
    } else if (!textNode.textContent) {
      textNode.textContent = "\u200B";
    }

    const range = document.createRange();
    range.setStart(textNode, 0);
    range.collapse(true);
    selection.addRange(range);
    return;
  }

  const range = document.createRange();
  range.selectNodeContents(target);
  selection.addRange(range);
};

const updateOptionLabel = (event: Event, index: number, isOther: boolean) => {
  if (isOther) return;

  const target = event.target as HTMLElement;
  const caret = getCaretOffsetWithin(target);
  const label = target.textContent?.replace(/\u200B/g, "") ?? "";

  const option = props.element.picklistOptionsImage[index];
  if (!option) return;
  const nextOptions = [...props.element.picklistOptionsImage];
  nextOptions[index] = updatePictureChoiceOptionLabel(option, label);
  emit(
    "update:element",
    { ...props.element, picklistOptionsImage: nextOptions },
    false
  );
  hasUncommittedOptionLabelChanges = true;

  handleSafariCursor(event);
  nextTick(() => {
    const labelElement = labelRefs.value[index] ?? target;
    setCaretOffsetWithin(labelElement as unknown as HTMLElement, caret);
  });
};

const handleOptionLabelInput = withIMECheck(updateOptionLabel);
const handleOptionLabelCompositionEnd = handleCompositionEnd(updateOptionLabel);

const commitOptionLabelHistory = () => {
  if (!hasUncommittedOptionLabelChanges) return;

  hasUncommittedOptionLabelChanges = false;
  store.addToHistory();
};

onBeforeUnmount(commitOptionLabelHistory);

const handleMediaSelection = (
  index: number,
  data: PictureChoiceMediaSelection
) => {
  if (!data?.url || !props.element.picklistOptionsImage[index]) return;

  updateElement((element) => {
    const option = element.picklistOptionsImage[index];
    if (!option) return;

    element.picklistOptionsImage[index] = applyPictureChoiceMediaSelection(
      option,
      data
    );
  });
};

const openMediaLibrary = (event: Event, index: number, isOther: boolean) => {
  if (!props.isActive || isOther) return;

  event.preventDefault();
  event.stopPropagation();
  openPictureChoiceMediaCenter({
    appHandshake: store.appHandshake,
    subCategory: store.mediaCenterSubCategory ?? MediaCenterSubCategory.FORM,
    onSelect: (data) => handleMediaSelection(index, data),
  });
};

const isEmptyMediaTrigger = (option: PictureChoiceOption, isOther: boolean) =>
  props.isActive && !isOther && !option.src;

const openEmptyMediaLibrary = (
  event: Event,
  index: number,
  option: PictureChoiceOption,
  isOther: boolean
) => {
  if (!isEmptyMediaTrigger(option, isOther)) return;
  openMediaLibrary(event, index, isOther);
};

const removeOption = (event: Event, index: number) => {
  event.preventDefault();
  event.stopPropagation();

  if (!props.isActive || props.element.picklistOptionsImage.length <= 1) {
    return;
  }

  const remainingOptionsCount = props.element.picklistOptionsImage.length - 1;
  focusedOptionIndex.value = null;

  updateElement((element) => {
    element.picklistOptionsImage.splice(index, 1);
  });

  nextTick(() => {
    const survivingIndex = Math.min(index, remainingOptionsCount - 1);
    (labelRefs.value[survivingIndex] ?? mediaRefs.value[survivingIndex])?.focus();
  });
};

const gridStyle = computed<CSSProperties>(() => ({
  gridTemplateColumns: props.element.spreadColumns
    ? `repeat(${clampPictureChoiceColumnsForDevice(
        props.element.columnsNumber,
        store.deviceMode
      )}, minmax(0, 1fr))`
    : "repeat(auto-fit, minmax(140px, max-content))",
}));

const imageStyle = computed<CSSProperties>(() => {
  return {
    aspectRatio: "4 / 3",
    height: "100%",
    objectFit: imageFit.value,
  };
});

const emptyImageStyle = computed<CSSProperties>(() => ({
  aspectRatio: "4 / 3",
  height: "100%",
  objectFit: "contain",
}));

const optionGroupName = computed(() =>
  String(
    props.element.id ??
      props.element.uuid ??
      `${props.element.tag ?? "picture-choice"}-${componentInstanceId}`
  )
);

const getOptionId = (index: number) => `${optionGroupName.value}-${index}`;
</script>

<template>
  <div class="flex w-full flex-col">
    <div
      data-testid="picture-choice-grid"
      class="grid w-full gap-4"
      :style="gridStyle"
    >
      <div
        v-for="({ option, isOther }, index) in options"
        :key="getOptionId(index)"
        data-testid="picture-choice-card"
        class="relative flex min-w-0 cursor-default flex-col overflow-hidden rounded-lg border border-gray-200 bg-white"
      >
        <!-- Empty media remains the direct picker trigger; filled media uses hover actions. -->
        <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
        <div
          :ref="(element) => setMediaRef(element, index)"
          data-testid="picture-choice-option-media"
          class="relative flex min-h-[88px] bg-gray-50"
          :role="isEmptyMediaTrigger(option, isOther) ? 'button' : undefined"
          :tabindex="
            isEmptyMediaTrigger(option, isOther)
              ? 0
              : isActive && !isOther
              ? -1
              : undefined
          "
          :aria-label="
            isEmptyMediaTrigger(option, isOther)
              ? t('common.uploadImage')
              : undefined
          "
          :class="[
            'items-center justify-center',
            isEmptyMediaTrigger(option, isOther) ? 'cursor-pointer' : '',
          ]"
          @click="openEmptyMediaLibrary($event, index, option, isOther)"
          @keydown.enter.self="
            openEmptyMediaLibrary($event, index, option, isOther)
          "
          @keydown.space.self.prevent
          @keyup.space.self="
            openEmptyMediaLibrary($event, index, option, isOther)
          "
          @mouseenter="hoveredOptionIndex = index"
          @mouseleave="hoveredOptionIndex = null"
          @focusin="focusedOptionIndex = index"
          @focusout="focusedOptionIndex = null"
        >
          <UITooltip v-if="isOther" trigger="hover" placement="top">
            <template #trigger>
              <span
                data-testid="picture-choice-other-image-tooltip-trigger"
                class="flex h-full w-full items-center justify-center"
                tabindex="0"
                :aria-label="
                  t(
                    'fields.choiceElements.pictureChoice.otherImageUploadUnavailable'
                  )
                "
              >
                <img
                  :src="otherOptionImage"
                  alt=""
                  data-testid="picture-choice-other-image"
                  aria-hidden="true"
                  class="block max-h-full w-full"
                  :style="emptyImageStyle"
                />
              </span>
            </template>
            {{
              t(
                "fields.choiceElements.pictureChoice.otherImageUploadUnavailable"
              )
            }}
          </UITooltip>
          <img
            v-else-if="option.src"
            :src="option.src"
            :alt="option.altText"
            class="pointer-events-none block max-h-full w-full"
            :style="imageStyle"
          />
          <img
            v-else
            :src="emptyOptionImage"
            alt=""
            data-testid="picture-choice-empty-image"
            aria-hidden="true"
            class="block max-h-full w-full"
            :style="emptyImageStyle"
          />

          <div
            v-if="isActive && !isOther && option.src"
            data-testid="picture-choice-filled-image-actions"
            class="absolute right-2 top-2 z-10 flex gap-1 transition-opacity"
            :class="
              isFilledImageActionsVisible(index)
                ? 'pointer-events-auto opacity-100'
                : 'pointer-events-none opacity-0'
            "
          >
            <UITooltip trigger="hover" placement="top">
              <template #trigger>
                <UIButton
                  :data-testid="`picture-choice-change-image-${index}`"
                  :aria-label="t('common.changeImage')"
                  class="!h-9 !w-9 !min-w-[36px] !rounded-lg !border !border-gray-300 !bg-white !p-2 !text-gray-700 shadow-sm hover:!bg-gray-50 hover:!text-gray-800"
                  @click.stop="openMediaLibrary($event, index, isOther)"
                >
                  <Image01Icon class="h-5 w-5" />
                </UIButton>
              </template>
              {{ t("common.changeImage") }}
            </UITooltip>

            <UITooltip
              v-if="element.picklistOptionsImage.length > 1"
              trigger="hover"
              placement="top"
            >
              <template #trigger>
                <UIButton
                  :data-testid="`picture-choice-delete-option-${index}`"
                  :aria-label="
                    t('fields.choiceElements.pictureChoice.deleteOption')
                  "
                  class="!h-9 !w-9 !min-w-[36px] !rounded-lg !border !border-gray-300 !bg-white !p-2 !text-gray-700 shadow-sm hover:!bg-gray-50 hover:!text-gray-800"
                  @click.stop="removeOption($event, index)"
                >
                  <Trash01Icon class="h-5 w-5" />
                </UIButton>
              </template>
              {{ t("fields.choiceElements.pictureChoice.deleteOption") }}
            </UITooltip>
          </div>
        </div>
        <div
          data-testid="picture-choice-option-label"
          class="flex min-w-0 items-center gap-2 px-3 py-2"
        >
          <input
            :id="getOptionId(index)"
            :type="inputType"
            :name="optionGroupName"
            :aria-label="option.label"
            disabled
            class="!static !m-0 !h-4 !w-4 !shrink-0 !appearance-none !border !border-gray-300 !bg-white"
            :class="inputType === 'radio' ? '!rounded-full' : '!rounded'"
          />
          <span
            v-if="showOptionLabel && isOther"
            data-testid="picture-choice-option-label-text"
            class="min-w-0 break-words py-1 text-sm font-medium text-gray-700"
          >
            {{ option.label }}
          </span>
          <!-- Editable text needs pointer handling while active; keyboard editing is handled below. -->
          <!-- eslint-disable-next-line vuejs-accessibility/no-static-element-interactions -->
          <span
            v-else-if="showOptionLabel"
            :id="`${getOptionId(index)}-label`"
            :ref="(element) => setLabelRef(element, index)"
            data-testid="picture-choice-option-label-text"
            :role="isActive ? 'textbox' : undefined"
            :aria-readonly="isActive ? 'false' : undefined"
            :tabindex="isActive ? 0 : undefined"
            :contenteditable="isActive ? 'true' : undefined"
            class="min-w-0 break-words py-1 text-sm font-medium text-gray-700"
            :class="isActive ? 'cursor-text outline-none' : ''"
            @click.stop
            @focus="handleOptionLabelFocus($event, isOther)"
            @blur="commitOptionLabelHistory"
            @compositionstart="handleCompositionStart"
            @compositionend="
              handleOptionLabelCompositionEnd($event, index, isOther)
            "
            @input.stop="handleOptionLabelInput($event, index, isOther)"
            @keydown="
              (event) => event.key !== 'Tab' && handleSafariCursor(event)
            "
            @keydown.enter="handleOptionLabelEnter($event, index, isOther)"
          >
            {{ option.label }}
          </span>
        </div>
      </div>
    </div>

    <div
      v-if="isActive"
      data-testid="picture-choice-actions"
      class="mt-2 flex flex-wrap items-center gap-2"
    >
      <UIButton
        data-testid="picture-choice-preview-add-option"
        text
        @click.stop="addOption"
      >
        <PlusIcon class="mr-1.5 h-5 w-5" />
        {{ t("fields.choiceElements.pictureChoice.addOption") }}
      </UIButton>
    </div>
  </div>
</template>
